"use client";

import { useEffect, useRef, useState } from "react";

export interface SortOption {
  label: string;
  value: string;
}

interface SortDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SortOption[];
  isDisabled?: boolean;
  className?: string;
}

export default function SortDropdown({
  value,
  onChange,
  options,
  isDisabled = false,
  className = "",
}: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLabel =
    options.find((o) => o.value === value)?.label ?? options[0]?.label ?? "Sort";

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        className={`text-[13px] flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-background hover:bg-accent transition-colors ${
          isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) setIsOpen(!isOpen);
        }}
        disabled={isDisabled}
        aria-label="Sort options"
        title="Sort options"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-foreground"
        >
          <path
            d="M3 9L7 5M7 5L11 9M7 5V19M16 15L12 19M16 15L20 19M16 15V5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="whitespace-nowrap text-foreground">{currentLabel}</span>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="text-[13px] absolute right-0 z-50 mt-1 w-[180px] bg-popover text-popover-foreground rounded-md shadow-lg border border-border py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`px-3 py-1.5 hover:bg-accent cursor-pointer flex items-center justify-between ${
                option.value === value ? "bg-accent font-medium" : "font-normal"
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
